/*
<summary>
   This functional component is all about rendering the Validate Tool popup for
   updating a tool's calibration/usage expiry date.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Field, Form, Formik } from 'formik';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import * as Yup from 'yup';
import CalibrationStatusEnum from '../../../constants/enums/calibration-status-enum';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikDateFieldMUI from '../../../shared-components/FormikFormHandlers/FormikDateFieldMUI';

interface IProps {
  toolManagementNo: string;
  currentExpiryDate: string | null;
  noOfDaysUntilWarning: number | null;
  isLoading: boolean;
  modalClosed: () => void;
  modalSubmit: (
    calibrationExpiryDate: string,
    calibrationStatus: number | null
  ) => void;
}

/**
* This function is used to derive the Calibration Status from the new expiry date and the
* tool's "No. of Days Until Warning" - the same rules Add/Edit Tool applies
* Returns null (no status calculated) when the warning days are above 2000.
* @param expiryDate: New Calibration / Use-By date
* @param noOfDaysUntilWarning: Tool's warning days
*/
const calculateCalibrationStatus = (
  expiryDate: Date,
  noOfDaysUntilWarning: number | null
): number | null => {
  const warningDays = Number(noOfDaysUntilWarning ?? 0);
  if (isNaN(warningDays) || warningDays > 2000) return null;

  const expiry = new Date(expiryDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);

  const warningStartDate = new Date(expiry);
  warningStartDate.setDate(warningStartDate.getDate() - warningDays);

  if (today > expiry) return CalibrationStatusEnum.Expired;
  if (today > warningStartDate) return CalibrationStatusEnum.Warning;
  return CalibrationStatusEnum.Valid;
};

/**
* This function is used to turn the API's ISO datetime string into the Date the shared date
* picker (the same one Add Tool uses) works with, defaulting to today when there's no
* previously-set expiry date to show.
* @param value: ISO Datetime String
*/
const toPickerDate = (value: string | null): Date => {
  if (!value) return new Date();
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
};

/**
* This function is used to format the picked Date as the "YYYY-MM-DD" string the API expects,
* using the local calendar day so the picked date doesn't shift with the timezone.
* @param date: Picked Date
*/
const toApiDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const ValidateToolSchema = Yup.object().shape({
  CalibrationExpiryDate: Yup.date()
    .typeError('invalid_date_format')
    .required('field_required'),
});

/**
* This functional component renders the Validate Tool popup and submits the updated
* calibration/usage expiry date for the selected tool.
* @param props: Tool management number, current expiry date, loading state and handlers
*/
const ValidateToolPopup = (props: IProps) => {
  return (
    <Modal
      show={true}
      size='lg'
      centered
      className="update-callibrartion-modal"
    >
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage id="update_calibration_usage_expiry" /> - {props.toolManagementNo}
          <button
            onClick={props.modalClosed}
            className="Crossicons"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              fill="currentColor"
              className="bi bi-x"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        </Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={{
          CalibrationExpiryDate: toPickerDate(props.currentExpiryDate),
        }}
        validationSchema={ValidateToolSchema}
        validateOnBlur={false}
        onSubmit={(values) => {
          const expiryDate = new Date(values.CalibrationExpiryDate);
          props.modalSubmit(
            toApiDate(expiryDate),
            calculateCalibrationStatus(expiryDate, props.noOfDaysUntilWarning)
          );
        }}
      >
        {() => (
          <Form>
            <Modal.Body>
              <div className="form-group mb-0">
                <label className="label-style col-form-label">
                  <FormattedMessage id="calibration_usage_expiry" />
                </label>
                <Field
                  name="CalibrationExpiryDate"
                  as={FormikDateFieldMUI}
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="light-danger"
                onClick={props.modalClosed}
                disabled={props.isLoading}
              >
                <FormattedMessage id="cancel" />
              </Button>
              <LoaderButton
                type="submit"
                isLoading={props.isLoading}
                text="validation"
              />
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ValidateToolPopup;
